import { FontAwesome } from '@expo/vector-icons';
import React, { useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { TextInput } from 'react-native-gesture-handler';
import Colors from '../../constants/Colors';
import DeleteIcon16 from '../16pxIcons/DeleteIcon16';
import CloseModalIcon from '../CloseModalIcon';
import Style from '../../constants/Style';
import editUser from '../../requests/Users/EditUser';
import AlertModal from '../AlertModal';
import { logEvent } from '../LogEvent';
import { OOPS_SOMETHING_WENT_WRONG } from '../../constants/AlertMessages';
import PressableStyled, { PressableState } from '../PressableStyled';

export default function EditUser(props) {
  const [roleDropdownVisible, setRoleDropdownVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagsUpdated, setTagsUpdated] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertError, setAlertError] = useState(true);
  const [alertTitle, setAlertTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const roles = props.roles ?? [];

  const showTags = false;

  const getTestID = (role) => {
    switch (role) {
      case 'Admin':
        return 'um-admin-role-btn';
      case 'Non Admin':
        return 'um-non-admin-role-btn';
      default:
        return 'um-other-role-btn';
    }
  };

  const RenderSaveCancel = () => (
    <View
      style={{
        flexDirection: 'row',
        marginVertical: props.mobile ? 30 : 18,
      }}
    >
      <PressableStyled
        style={{
          backgroundColor: Colors.light.hereworksLightBlue,
          borderRadius: 4,
          marginRight: 12,
        }}
        disabled={!selectedRole}
        onPress={() => {
          logEvent('EditUser', 'Save');
          setLoading(true);
          editUser(
            props.user.email,
            props.user.firstName,
            props.user.lastName,
            selectedRole === 'Admin',
            selectedRole === 'Admin' ? null : selectedRole,
          ).then((res) => {
            if (res?.isError) {
              setAlertTitle(OOPS_SOMETHING_WENT_WRONG);
              setAlertError(true);
            } else {
              setAlertTitle(res?.message ?? 'User edited successfully!');
              setAlertError(false);
              props.roleChanged();
            }
            setAlertVisible(true);
          }).finally(() => setLoading(false));
        }}
      >
        <Text
          style={{
            color: '#fff',
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            marginVertical: 8,
            marginHorizontal: 24,
          }}
        >
          Save
        </Text>
      </PressableStyled>
      <PressableStyled
        style={{
          backgroundColor: '#fff',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: Colors.light.hereworksLightBlue,
        }}
        onPress={() => {
          logEvent('EditUser', 'Cancel');
          setSelectedRole(null);
          setSelectedRoleName(null);
          props.setEditModalVisible(false);
        }}
      >
        <Text
          style={{
            color: Colors.light.hereworksLightBlue,
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            marginVertical: 8,
            marginHorizontal: 24,
          }}
        >
          Cancel
        </Text>
      </PressableStyled>
      {loading && <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#00000020',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <ActivityIndicator size='large' color={Colors.light.hereworksLightBlue} />
      </View>}
    </View>
  );

  const renderTag = (title: string) => (
    <TouchableOpacity
      style={{
        backgroundColor: Colors.light.hereworksYellow50,
        borderColor: Colors.light.hereworksDarkYellow700,
        borderWidth: 1,
        borderRadius: 4,
        marginRight: 10,
        marginBottom: 10,
        flexDirection: 'row',
        alignItems: 'center',
      }}
      onPress={() => {
        setTags(tags.filter((tag) => tag !== title));
        setTagsUpdated(!tagsUpdated);
        logEvent('EditUser', 'Tag');
      }}
    >
      <Text
        style={{
          color: Colors.light.hereworksDarkYellow700,
          fontFamily: 'Roboto_500Medium',
          fontSize: 12,
          lineHeight: 14,
          marginLeft: 8,
          marginVertical: 5.5,
        }}
      >
        {title}
      </Text>
      <DeleteIcon16
        color={Colors.light.hereworksDarkYellow700}
        style={{ marginHorizontal: 4 }}
      />
    </TouchableOpacity>
  );

  return (
    <Modal
      visible={props.editModalVisible}
      style={{ alignItems: 'center' }}
      onRequestClose={() => props.setEditModalVisible(false)}
      animationType={props.mobile ? 'slide' : 'fade'}
      transparent={true}
    >
      <Pressable
        style={{
          flex: 1,
          backgroundColor: '#00000020',
          justifyContent: props.mobile ? 'flex-end' : 'center',
          alignItems: 'center',
          paddingHorizontal: props.mobile ? 0 : 100,
          paddingVertical: props.mobile ? 0 : 50,
          cursor: 'auto',
        }}
        onPress={() => {
          props.setEditModalVisible(false);
          logEvent('EditUser', 'Close_Modal');
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'position' : 'height'}
        >
          <Pressable
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 10,
              backgroundColor: '#fff',
              cursor: 'auto',
            }}
          >
            <Text
              style={{
                fontFamily: 'Poppins_600SemiBold',
                fontSize: props.mobile ? 20 : 28,
                marginBottom: 15,
                marginTop: props.mobile ? 24 : 24,
                paddingHorizontal: props.mobile ? 71 : 90,
                textAlign: 'center',
              }}
            >
              Give a role to the member
            </Text>
            <Text
              style={{
                fontSize: props.mobile ? 16 : 20,
                fontFamily: 'Poppins_500Medium',
                color: Colors.light.hereworksBlack600,
                marginTop: 4,
                marginBottom: 16,
              }}
            >
              {`${props.user.firstName ? props.user.firstName : props.user.email} ${
                props.user.lastName ?? ''
              }`}
            </Text>
            <View
              style={{
                alignSelf: 'stretch',
                marginHorizontal: props.mobile ? 16 : 40,
              }}
            >
              <Text style={styles.smallHeader}>Role</Text>
              <TouchableOpacity
                style={styles.dropdownBox}
                onPress={() => {
                  setRoleDropdownVisible(!roleDropdownVisible);
                  logEvent(
                    'EditUser',
                    `Role_dropdown_${roleDropdownVisible ? 'Close' : 'Open'}`,
                  );
                }}
              >
                <Text style={styles.dropdownBoxText}>
                  {selectedRoleName ?? 'Choose role'}
                </Text>
                <View style={{ flex: 1, alignItems: 'flex-end' }}>
                  <FontAwesome
                    size={20}
                    style={{
                      color: Colors.light.hereworksLightBlue,
                      marginRight: 12,
                    }}
                    name={roleDropdownVisible ? 'angle-up' : 'angle-down'}
                  />
                </View>
              </TouchableOpacity>
              {roleDropdownVisible && (
                <>
                  <ScrollView style={styles.dropdownView}>
                    <Pressable
                      key={0}
                      onPress={() => {
                        setSelectedRole('Admin');
                        setSelectedRoleName('Admin');
                        setRoleDropdownVisible(!roleDropdownVisible);
                        logEvent('EditUser', 'Selected_group');
                      }}
                      testID={getTestID('Admin')}
                    >
                      <Text style={styles.dropdownItemText}>Admin</Text>
                    </Pressable>
                    {roles.map((role, key) => (
                      <Pressable
                        key={key}
                        onPress={() => {
                          setSelectedRole(role.id);
                          setSelectedRoleName(role.name);
                          setRoleDropdownVisible(!roleDropdownVisible);
                          logEvent('EditUser', 'Selected_group');
                        }}
                        testID={getTestID(role.name)}
                      >
                        <Text style={styles.dropdownItemText}>{role.name}</Text>
                      </Pressable>
                    ))}
                  </ScrollView>
                </>
              )}
              {showTags && (
                <>
                  <Text style={styles.smallHeader}>Add tag</Text>
                  <TextInput
                    style={styles.noteText}
                    placeholderTextColor={Colors.light.hereworksBlack400}
                    placeholder={'Enter tag'}
                    onSubmitEditing={(text) => {
                      const exTags = tags;
                      exTags.push(text.nativeEvent.text);
                      setTags(exTags);
                      setTagsUpdated(!tagsUpdated);
                    }}
                  />
                  <View
                    style={{
                      flexWrap: 'wrap',
                      flexDirection: 'row',
                      marginTop: 7,
                    }}
                  >
                    {tags.map((tag, key) => (
                      <View key={key}>{renderTag(tag)}</View>
                    ))}
                  </View>
                </>
              )}
            </View>
            <RenderSaveCancel />
            <Pressable
              onPress={() => {
                logEvent('EditUser', 'Close_Modal');
                setSelectedRole(null);
                setSelectedRoleName(null);
                props.setEditModalVisible(false);
              }}
              style={({ hovered }: PressableState) => [
                Style.closeButton,
                {
                  backgroundColor: hovered
                    ? Colors.light.hereworksBlue200
                    : Colors.light.hereworksBlue50,
                },
              ]}
              testID="um-close-edit-user-modal-btn"
            >
              <CloseModalIcon />
            </Pressable>
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
      {!props.mobile && <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        setModalVisible={setAlertVisible}
        action={() => props.setEditModalVisible(false)}
      />}
    </Modal>
  );
}

const styles = StyleSheet.create({
  noteText: {
    color: Colors.light.hereworksPrimaryGrey,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    backgroundColor: Colors.light.hereworksWhite200,
    alignContent: 'center',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 6,
    verticalAlign: 'top',
    outlineWidth: 0,
  },
  smallHeader: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    alignSelf: 'flex-start',
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    borderRadius: 10,
    marginTop: 5,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 1,
  },
  dropdownView: {
    width: '100%',
    marginTop: 5,
    zIndex: 5,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    maxHeight: 150,
    backgroundColor: '#fff',
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 21,
    color: Colors.light.hereworksPrimaryGrey,
    marginVertical: 9.5,
    marginLeft: 12,
  },
});
